// User-defined severity bands and colours (Criteria dialog). Twin of
// Backend/123bimlive/utils/overview_data.py: classify_value / apply_severity — the
// PDF receives these settings and must classify the same way.
//
// Every analysis has a measured value per site; two cuts split it into minor /
// moderate / severe. `sense` says which way is worse: 'low' = value at or below the
// cut (a speed shortfall, a sight-distance quality), 'high' = value at or above it
// (an embankment height, glare hours). The defaults reproduce what the reports do.
import { SEVERITY_COLORS } from './overviewModel';

export const STORAGE_KEY = 'overview:severity';

export const SEVERITY_DEFAULTS = {
  colors: { severe: '#e03131', moderate: '#f08c00', minor: '#fcc419' },
  thresholds: {
    SSD: { severe: -15, moderate: -10 },
    HCA: { severe: 80, moderate: 90 },
    ISD: { severe: 50, moderate: 80 },
    GRW: { severe: 10, moderate: 5 },
    GLR: { severe: 160, moderate: 160 },
    FSA: { severe: -1, moderate: 0.5 },
  },
};

/** Analyses whose every deficiency has ONE severity, with no cuts to set: a PSD Unsafe pass
 * (the marking allows passing where the passing sight distance is not available) is always severe. */
export const SEVERITY_FIXED = { PSD: 'severe' };

/** How each analysis is measured: what the value is, its unit and which way is worse. */
export const SEVERITY_SPECS = {
  SSD: { sense: 'low', unit: 'mph', label: 'Speed shortfall vs posted (negative)', hint: 'severe at or below the first cut, moderate at or below the second, else minor' },
  HCA: { sense: 'low', unit: '%', label: 'Curve safety score (safe speed ÷ posted)', hint: 'severe at or below the first cut, moderate at or below the second' },
  ISD: { sense: 'low', unit: '%', label: 'Sight-distance quality (available ÷ required)', hint: 'severe at or below the first cut, moderate at or below the second; 100% and above is no deficiency' },
  GRW: { sense: 'high', unit: 'ft', label: 'Embankment height where a guardrail is warranted', hint: 'severe at or above the first cut, moderate at or above the second' },
  GLR: { sense: 'high', unit: 'h/yr', label: 'Glare hours per year', hint: 'severe at or above the first cut, moderate at or above the second; the reports list stations from 160 h' },
  FSA: { sense: 'low', unit: '%', label: 'Gradient of a flat spot (resultant of cross and long slope)', hint: 'severe at or below the first cut (−1 = never), moderate at or below the second, else minor' },
};

/** The measured value of a site (or intersection) in the unit of SEVERITY_SPECS. */
export function measureOf(analysis, site) {
  const v = site.worstValue;
  switch (analysis) {
    case 'ISD': return typeof site.meanValue === 'number' ? site.meanValue * 100 : (typeof site.quality === 'number' ? site.quality * 100 : null);
    case 'GLR': return typeof v === 'number' ? v / 60 : null;
    case 'FSA': return typeof v === 'number' ? Math.abs(v) : null;
    default: return typeof v === 'number' ? v : null;
  }
}

export function classifyValue(analysis, value, thresholds) {
  if (SEVERITY_FIXED[analysis]) return SEVERITY_FIXED[analysis];
  const t = (thresholds || SEVERITY_DEFAULTS.thresholds)[analysis];
  const spec = SEVERITY_SPECS[analysis];
  if (!t || !spec || value == null || Number.isNaN(value)) return null;
  if (spec.sense === 'low') {
    if (value <= t.severe) return 'severe';
    if (value <= t.moderate) return 'moderate';
    return 'minor';
  }
  if (value >= t.severe) return 'severe';
  if (value >= t.moderate) return 'moderate';
  return 'minor';
}

/** Segments with every site's (and intersection's) severity re-derived from the settings. */
export function applySeverity(segments, settings) {
  const thresholds = (settings && settings.thresholds) || SEVERITY_DEFAULTS.thresholds;
  return segments.map((seg) => ({
    ...seg,
    // a PSD site graded by its reasons (user rules) keeps the server's severity
    sites: (seg.sites || []).map((s) => ((s.reasons && SEVERITY_FIXED[s.analysis]) ? s
      : { ...s, severity: classifyValue(s.analysis, measureOf(s.analysis, s), thresholds) || s.severity })),
    intersections: (seg.intersections || []).map((ix) => {
      if (typeof ix.quality !== 'number' || ix.quality >= 1) return { ...ix, severity: null };
      return { ...ix, severity: classifyValue('ISD', ix.quality * 100, thresholds) || ix.severity };
    }),
  }));
}

const sane = (raw) => {
  const out = { colors: { ...SEVERITY_DEFAULTS.colors }, thresholds: {} };
  Object.keys(SEVERITY_DEFAULTS.thresholds).forEach((a) => {
    const d = SEVERITY_DEFAULTS.thresholds[a];
    const r = (raw && raw.thresholds && raw.thresholds[a]) || {};
    out.thresholds[a] = {
      severe: Number.isFinite(Number(r.severe)) ? Number(r.severe) : d.severe,
      moderate: Number.isFinite(Number(r.moderate)) ? Number(r.moderate) : d.moderate,
    };
  });
  ['severe', 'moderate', 'minor'].forEach((k) => {
    const c = raw && raw.colors && raw.colors[k];
    if (typeof c === 'string' && /^#[0-9a-f]{6}$/i.test(c)) out.colors[k] = c;
  });
  return out;
};

export function loadSeveritySettings() {
  try { return sane(JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null')); } catch (e) { return sane(null); }
}

export function saveSeveritySettings(settings) {
  const s = sane(settings);
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(s)); } catch (e) { /* private window */ }
  return s;
}

/** Make the chosen colours the ones every component reads (SEVERITY_COLORS is shared). */
export function applySeverityColors(settings) {
  Object.assign(SEVERITY_COLORS, (settings && settings.colors) || SEVERITY_DEFAULTS.colors);
  return SEVERITY_COLORS;
}

export const isDefaultSettings = (s) => JSON.stringify(sane(s)) === JSON.stringify(sane(null));
/** The built-in criteria (the Safety Overview's customer modes use these, whatever this browser saved). */
export const defaultSeveritySettings = () => sane(null);
